import React from 'react';
import { User } from '@/types';

interface DeleteUserModalProps {
    onClose: () => void;
    onConfirm: () => Promise<boolean>;
    user: User;
}

/**
 * Confirmation modal component for deleting users
 */
const DeleteUserModal: React.FC<DeleteUserModalProps> = ({ onClose, onConfirm, user }) => {
    const [loading, setLoading] = React.useState(false);
    const [error, setError] = React.useState('');
    const [success, setSuccess] = React.useState('');

    const handleConfirm = async () => {
        setError('');
        setSuccess('');
        try {
            setLoading(true);
            const ok = await onConfirm();
            if (ok) {
                setSuccess('User deleted successfully.');
                setTimeout(() => onClose(), 800);
            } else {
                setError('Failed to delete user. Please try again.');
            }
        } catch (e) {
            console.error('Error deleting user:', e);
            setError(e instanceof Error ? e.message : 'Failed to delete user. Please try again.');
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="cgm-modal-backdrop">
            <div className="flex items-center justify-center min-h-screen p-4">
                <div className="cgm-card cgm-modal-panel p-8 max-w-md w-full">
                    <div className="text-center mb-6">
                        <h3 className="text-2xl cgm-text-primary mb-2">
                            Delete User
                        </h3>
                        <p className="cgm-text-secondary text-sm">
                            Are you sure you want to delete <strong>{user.full_name || user.username}</strong>? This action cannot be undone.
                        </p>
                    </div>

                    {error && (
                        <div className="cgm-alert-error px-4 py-3 rounded-md mb-4 text-sm">{error}</div>
                    )}
                    {success && (
                        <div className="cgm-alert-success px-4 py-3 rounded-md mb-4 text-sm">{success}</div>
                    )}

                    <div className="flex space-x-4">
                        <button
                            onClick={handleConfirm}
                            disabled={loading}
                            className="cgm-button flex-1 py-3 px-4 flex items-center justify-center"
                        >
                            {loading ? (
                                <>
                                    <div className="animate-spin rounded-full h-5 w-5 mr-3 cgm-spinner border-2"></div>
                                    Deleting...
                                </>
                            ) : (
                                'Delete User'
                            )}
                        </button>
                        <button
                            onClick={onClose}
                            disabled={loading}
                            className="cgm-cancel-button flex-1 py-3 px-4 flex items-center justify-center rounded-lg font-medium"
                        >
                            Cancel
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default DeleteUserModal; 